import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Navigate } from "react-router-dom";
import { UserSearch, Search, Camera, X, ImagePlus, Users, Mail, Phone, IdCard, Calendar, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

const simColor = (s) =>
  s >= 0.6 ? "text-emerald-500" : s >= 0.45 ? "text-amber-500" : "text-muted-foreground";

function SimilarityBar({ value }) {
  const p = Math.round(value * 100);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary" style={{ width: `${p}%` }} />
      </div>
      <span className={`text-sm font-semibold tabular-nums ${simColor(value)}`}>{p}%</span>
    </div>
  );
}

const statusPill = (s) => {
  const map = {
    passed: "bg-emerald-500/15 text-emerald-500",
    failed: "bg-red-500/15 text-red-500",
    pending: "bg-amber-500/15 text-amber-500",
    expired: "bg-muted text-muted-foreground",
  };
  return map[s] || "bg-muted text-muted-foreground";
};

// Detail modal: identity, reference photos and linked liveness verifications.
function PersonModal({ data, loading, onClose }) {
  const { t } = useI18n();
  const ident = data ? (data.name || data.document || (data.emails || [])[0] || "—") : "";
  const Row = ({ icon: Icon, label, value }) =>
    value ? (
      <div className="flex items-start gap-2 text-sm">
        <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
        <span className="text-muted-foreground">{label}:</span>
        <span className="font-medium break-all">{value}</span>
      </div>
    ) : null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] overflow-y-auto border border-border"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <UserSearch className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">{loading ? t("common.loading", "Carregando…") : ident}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading || !data ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                {t("people.identity", "Identidade")}
              </h3>
              <Row icon={Mail} label={t("common.email", "E-mail")} value={(data.emails || []).join(", ")} />
              <Row icon={IdCard} label={t("people.colDocument", "Documento")}
                value={data.document ? `${(data.document_type || "").toUpperCase()} ${data.document}` : ""} />
              <Row icon={Phone} label={t("common.phone", "Telefone")} value={data.mobile_phone} />
              <Row icon={Calendar} label={t("people.createdAt", "Cadastrado em")}
                value={data.created ? new Date(data.created).toLocaleString() : ""} />
            </div>

            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                {t("people.colPhotos", "Fotos")} ({(data.photos_list || []).length})
              </h3>
              {(data.photos_list || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("people.noPhotos", "Sem fotos de referência.")}</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {data.photos_list.map((ph) => (
                    <figure key={ph.id} className="text-center">
                      <img src={ph.image} alt="ref" className="h-28 w-24 object-cover rounded-lg border border-border bg-muted" />
                      <figcaption className="text-[11px] text-muted-foreground mt-1">
                        {Math.round((ph.det_score || 0) * 100)}%
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                {t("people.linkedSessions", "Verificações vinculadas")} ({(data.sessions || []).length})
              </h3>
              {(data.sessions || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("people.noSessions", "Nenhuma verificação vinculada.")}</p>
              ) : (
                <div className="divide-y divide-border rounded-lg border border-border">
                  {data.sessions.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <span className="block font-medium truncate">{s.subject_name || s.subject_email || s.id}</span>
                        <span className="block text-xs text-muted-foreground">
                          {s.created ? new Date(s.created).toLocaleString() : "—"}
                        </span>
                      </div>
                      <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${statusPill(s.status)}`}>
                        <ShieldCheck className="h-3 w-3" /> {s.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Admin → People (Super Admin only): the biometric registry, plus a face search
// that ranks people by similarity to an uploaded photo.
export default function People() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchMode, setSearchMode] = useState(false); // face-search results shown?
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [faces, setFaces] = useState([]); // extracted faces + per-face matches
  const [selFace, setSelFace] = useState(0);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const debounceRef = useRef(null);
  const fileRef = useRef(null);

  const fetchList = useCallback(async (query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      const res = await api.get(`/api/people/?${params.toString()}`);
      setRows(res.data.results || []);
      setCount(res.data.count || 0);
      setSearchMode(false);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) fetchList("");
  }, [user, fetchList]);

  if (user && !user.is_admin) return <Navigate to="/liveness/sessions" replace />;

  const onSearchText = (v) => {
    setQ(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchList(v), 350);
  };

  const onPhoto = async (file) => {
    if (!file) return;
    setSearching(true); setSearchErr("");
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const res = await api.post("/api/people/search/", fd);
      const fc = res.data.faces || [];
      setFaces(fc);
      setSelFace(0);
      setCount(fc.reduce((n, f) => n + (f.count || 0), 0));
      setSearchMode(true);
    } catch (e) {
      setSearchErr(e?.response?.status === 422
        ? t("people.errNoFace", "Nenhum rosto utilizável na foto enviada.")
        : (e?.response?.data?.detail || t("people.errSearchFailed", "Falha na busca por foto.")));
    } finally {
      setSearching(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearSearch = () => { setSearchMode(false); setFaces([]); fetchList(q); };

  const openPerson = async (id) => {
    setDetailId(id); setDetail(null); setDetailLoading(true);
    try {
      const res = await api.get(`/api/people/${id}/`);
      setDetail(res.data);
    } catch { /* keep modal open, shows empty */ }
    finally { setDetailLoading(false); }
  };
  const closePerson = () => { setDetailId(null); setDetail(null); };

  const ident = (p) => p.name || p.document || (p.emails || [])[0] || "—";

  // In face-search mode the table shows the SELECTED face's matches; otherwise the list.
  const displayRows = searchMode ? (faces[selFace]?.results || []) : rows;

  return (
    <div className="animate-fade-in w-full">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <UserSearch className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">{t("people.title", "Pessoas")}</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {searchMode
              ? t("people.facesMatchCount", "{f} rosto(s) detectado(s) · {n} correspondência(s)")
                  .replace("{f}", faces.length).replace("{n}", count)
              : t("people.peopleCount", "{n} pessoa(s)").replace("{n}", count)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => onSearchText(e.target.value)}
              placeholder={t("people.searchPlaceholder", "Nome, documento, e-mail…")}
              className="h-10 w-64 rounded-lg border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => onPhoto(e.target.files?.[0])} />
          <Button onClick={() => fileRef.current?.click()} loading={searching}>
            <Camera className="h-4 w-4 mr-2" /> {t("people.searchByPhoto", "Buscar por foto")}
          </Button>
        </div>
      </div>

      {searchErr && <p className="text-sm text-red-500 mb-3">{searchErr}</p>}
      {searchMode && (
        <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
          <ImagePlus className="h-4 w-4" /> {t("people.sortedBySimilarity", "Resultados ordenados por similaridade facial.")}
          <button onClick={clearSearch} className="inline-flex items-center gap-1 text-primary hover:underline">
            <X className="h-3.5 w-3.5" /> {t("people.clear", "limpar")}
          </button>
        </div>
      )}

      {/* Extracted faces: every face detected in the uploaded image, each cropped and
          searched. Click one to see its matches below. */}
      {searchMode && faces.length > 0 && (
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 text-sm font-medium">
              <Users className="h-4 w-4 text-primary" />
              {t("people.extractedFaces", "Rostos extraídos da imagem")}
            </div>
            <div className="flex flex-wrap gap-3">
              {faces.map((f) => (
                <button
                  key={f.index}
                  onClick={() => setSelFace(f.index)}
                  title={t("people.faceHint", "Rosto {i} — {n} correspondência(s)")
                    .replace("{i}", f.index + 1).replace("{n}", f.count)}
                  className={`group relative rounded-lg overflow-hidden border-2 transition-all ${
                    selFace === f.index
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <img src={f.crop} alt={`face ${f.index + 1}`}
                    className="h-28 w-24 object-cover bg-muted" />
                  <span className="absolute top-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    #{f.index + 1}
                  </span>
                  <span className={`absolute bottom-0 inset-x-0 py-1 text-center text-[11px] font-semibold ${
                    f.count > 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  }`}>
                    {t("people.nMatches", "{n} match").replace("{n}", f.count)}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading || searching ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : displayRows.length === 0 ? (
            <div className="text-center py-12">
              <UserSearch className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {searchMode
                  ? t("people.faceNoMatch", "Nenhuma correspondência para o rosto selecionado.")
                  : t("people.empty", "Nenhuma pessoa encontrada.")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    {searchMode && <th className="text-left px-4 py-3 font-medium">{t("people.colSimilarity", "Similaridade")}</th>}
                    <th className="text-left px-4 py-3 font-medium">{t("people.colPerson", "Pessoa")}</th>
                    <th className="text-left px-4 py-3 font-medium">{t("people.colDocument", "Documento")}</th>
                    <th className="text-left px-4 py-3 font-medium">{t("common.phone", "Telefone")}</th>
                    <th className="text-left px-4 py-3 font-medium">{t("people.colPhotos", "Fotos")}</th>
                    <th className="text-left px-4 py-3 font-medium">{t("people.colValidations", "Validações")}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((p) => (
                    <tr key={p.id} onClick={() => openPerson(p.id)}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer">
                      {searchMode && (
                        <td className="px-4 py-3"><SimilarityBar value={p.similarity ?? 0} /></td>
                      )}
                      <td className="px-4 py-3">
                        <span className="block font-medium">{ident(p)}</span>
                        <span className="block text-muted-foreground text-xs">{(p.emails || []).join(", ")}</span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {p.document ? `${(p.document_type || "").toUpperCase()} ${p.document}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{p.mobile_phone || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{p.photos}</td>
                      <td className="px-4 py-3 text-muted-foreground">{p.validations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {detailId && <PersonModal data={detail} loading={detailLoading} onClose={closePerson} />}
    </div>
  );
}
